/**
 * SearchablePicker — a searchable single-select combobox for a bounded reference list (Room
 * Templates, Control Budget Models, etc.). Click to see the full list, type to filter by label,
 * click (or Enter on the top match) to pick. A document-level mousedown-outside listener closes
 * it, so selecting a row is a plain click — no onBlur race between closing the list and
 * registering the click.
 *
 * `items` is `null`/`undefined` while loading (shows "Loading…"), or an array of `{id, label}`.
 */
import { useEffect, useRef, useState } from 'react'

export default function SearchablePicker({ items, value, onChange, placeholder = 'Search…', inputStyle }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const selected = (items || []).find(t => t.id === value)

  useEffect(() => {
    if (!open) return
    function onDocMouseDown(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [open])

  const filtered = (items || []).filter(t => t.label.toLowerCase().includes(query.toLowerCase()))

  function pick(t) {
    onChange(t.id)
    setQuery('')
    setOpen(false)
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? query : (selected ? selected.label : '')}
        placeholder={items ? placeholder : 'Loading…'}
        onFocus={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setOpen(false); e.currentTarget.blur() }
          else if (e.key === 'Enter' && filtered.length) { e.preventDefault(); pick(filtered[0]) }
        }}
        style={inputStyle}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2, zIndex: 10,
          maxHeight: 220, overflowY: 'auto', background: 'var(--aj-bg-elevated)',
          border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}>
          {!items && (
            <div style={{ padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>
          )}
          {items && filtered.length === 0 && (
            <div style={{ padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>No matches</div>
          )}
          {filtered.map(t => (
            <div key={t.id} onMouseDown={e => { e.preventDefault(); pick(t) }}
              style={{
                padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, cursor: 'pointer',
                color: 'var(--aj-text)', background: t.id === value ? 'var(--aj-bg-subtle)' : 'transparent',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
              onMouseLeave={e => { e.currentTarget.style.background = t.id === value ? 'var(--aj-bg-subtle)' : 'transparent' }}
            >
              {t.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
